import React, {
  ReactElement,
  useEffect,
  useState,
  SyntheticEvent,
} from 'react';
import { Flex, Text, Box } from 'rebass/styled-components';

import FileImage from 'containers/ProcedureBuilder/FileImage';

import {
  Stack,
  RadioButton,
  TinyMCE,
  Dropzone,
  StyledSelect,
} from 'ui/components';
import { Loader } from 'ui/patterns';

import {
  useCreateBlobDirectory,
  useDeleteBlobFile,
  useGetProcedureUrlList,
  useUploadExternalDocument,
  useUploadFile,
} from 'hooks';

import { apiBaseUrl } from 'utils/urls';
import { showErrorToast } from 'utils/showToast';
import { useApplicationThemeStore } from '../../../../store/useApplicationThemeStore';

import { QuestionDetailsType } from '../questionTypes';
import { BlobType } from 'types/procedureBuilder';

export default function QuestionDetails(
  QuestionDetailsProps: QuestionDetailsType,
): ReactElement {
  const {
    selectedQuestionType,
    control,
    Controller,
    watch,
    setValue,
    accountId,
    accountInfoReturn,
  } = QuestionDetailsProps;

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showFileImage, setShowFileImage] = useState(false);

  const colorScheme = useApplicationThemeStore(
    React.useCallback((state: any) => state.colourScheme, []),
  );

  const questionDetailType = watch('QuestionDetailType');
  const helpText = watch('HelpText');

  const { data: procedures } = useGetProcedureUrlList(accountId);
  const { mutate: uploadExternalDocument, isLoading: isDocumentUploadLoading } =
    useUploadExternalDocument(setProgress);
  const {
    mutate: createBlobDirectory,
    isLoading: isCreateBlobDirectoryLoading,
  } = useCreateBlobDirectory();

  const { mutate: uploadFile, isLoading: isuploadFileLoading } =
    useUploadFile();

  const { mutate: deleteFile, isLoading: isDeleteFileLoading } =
    useDeleteBlobFile();

  useEffect(() => {
    if (selectedQuestionType?.QuestionDetail?.Type === questionDetailType)
      setValue(
        'QuestionDetailType',
        selectedQuestionType?.QuestionDetail?.Type,
        {
          shouldDirty: false,
        },
      );
  }, [selectedQuestionType, questionDetailType, setValue]);

  function toggleFileImage() {
    setShowFileImage(showFileImage => !showFileImage);
  }

  const isLoadingFile =
    isCreateBlobDirectoryLoading || isuploadFileLoading || isDeleteFileLoading;

  return (
    <>
      <Flex as="form" flexDirection="column" height="100%">
        {selectedQuestionType &&
          Object.keys(selectedQuestionType).length !== 0 ? (
          <Box>
            <Stack direction="x" gap={4} mb={4}>
              <Controller
                render={(props: {
                  onChange: (value: number) => void;
                  value: number;
                }) => {
                  return (
                    <RadioButton
                      onChange={() => {
                        props.onChange(0);
                      }}
                      inline
                      label="Text"
                      name="QuestionDetailType"
                      checked={questionDetailType === 0}
                    />
                  );
                }}
                defaultValue={0}
                name="QuestionDetailType"
                control={control}
              />

              <Controller
                render={(props: {
                  onChange: (value: number) => void;
                  value: number;
                }) => {
                  return (
                    <RadioButton
                      onChange={() => {
                        props.onChange(1);
                      }}
                      inline
                      label="Procedure"
                      name="QuestionDetailType"
                      checked={questionDetailType === 1}
                    />
                  );
                }}
                defaultValue={0}
                name="QuestionDetailType"
                control={control}
              />
              <Controller
                render={(props: {
                  onChange: (value: number) => void;
                  value: number;
                }) => {
                  return (
                    <RadioButton
                      onChange={() => {
                        props.onChange(2);
                      }}
                      inline
                      label="External Document"
                      name="QuestionDetailType"
                      checked={questionDetailType === 2}
                    />
                  );
                }}
                defaultValue={0}
                name="QuestionDetailType"
                control={control}
              />
            </Stack>

            <Controller
              render={(props: {
                onChange: (value: any) => void;
                value: any;
              }) => {
                if (questionDetailType !== 0) return null;
                return (
                  <TinyMCE
                    colorScheme={colorScheme}
                    toggleFileImage={toggleFileImage}
                    setLoading={setLoading}
                    height={240}
                    value={props.value}
                    name="HelpText"
                    onChange={(content: string) => {
                      props.onChange(content);
                    }}
                  />
                );
              }}
              defaultValue={
                selectedQuestionType?.QuestionDetail?.HelpText ?? ''
              }
              name="HelpText"
              control={control}
            />
            <Box maxWidth="305px">
              <Controller
                render={(props: {
                  onChange: (value: string) => void;
                  value: any;
                }) => {
                  if (questionDetailType !== 1) return null;
                  return (
                    <StyledSelect
                      onChange={(e: SyntheticEvent<HTMLSelectElement>) => {
                        props.onChange(e.currentTarget.value);
                      }}
                      value={props.value}
                    >
                      <option value="">Select one</option>
                      {procedures &&
                        procedures.Data.map((item: any, index: any) => (
                          <option key={index} value={item}>
                            {item}
                          </option>
                        ))}
                    </StyledSelect>
                  );
                }}
                defaultValue={
                  selectedQuestionType?.QuestionDetail?.ProcedureUri || ''
                }
                name="ProcedureUri"
                control={control}
              />
            </Box>

            <Controller
              render={(props: {
                onChange: (value?: string) => void;
                value: string;
              }) => {
                if (questionDetailType !== 2) return null;
                return (
                  <Dropzone
                    info="Please ensure this file type is associated with a program in the Citrix environment"
                    progress={progress}
                    value={props.value}
                    isLoading={isDocumentUploadLoading}
                    accountId={accountId}
                    onChange={(file?: any) => {
                      if (!file) props.onChange();
                      if (file && accountInfoReturn) {
                        const fileExtension = file?.name.split('.');
                        const validExtensions = [
                          'jpg',
                          'jpeg',
                          'png',
                          'svg',
                          'txt',
                          'docx',
                          'doc',
                          'pdf',
                          'html',
                          'htm',
                        ];
                        const checkCondtion = validExtensions.includes(
                          fileExtension[fileExtension.length - 1],
                        );
                        if (checkCondtion) {
                          uploadExternalDocument(
                            {
                              account: `${accountInfoReturn.CID}${accountInfoReturn.SID}${accountInfoReturn.PID}`,
                              filePath: `AccountFolders/${accountInfoReturn.CID}${accountInfoReturn.SID}${accountInfoReturn.PID}/Documents/${file.path}`,
                              file,
                            },
                            {
                              onSuccess: (filename: any) => {
                                props.onChange(filename);
                              },
                              onError: () => {
                                showErrorToast({
                                  message:
                                    'Files of this extension cannot be uploaded',
                                });
                              },
                            },
                          );
                        } else {
                          showErrorToast({
                            message:
                              'Files of this extension cannot be uploaded',
                          });
                        }
                      } else if (file) {
                        const reader = new FileReader();
                        reader.onabort = () =>
                          console.log('file reading was aborted');
                        reader.onerror = () =>
                          console.log('file reading has failed');
                        reader.onload = () => {
                          const base64String = reader.result;
                          if (typeof base64String === 'string') {
                            props.onChange(file.name);
                            // setExternalFile(base64String);
                          }
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                );
              }}
              defaultValue={selectedQuestionType?.QuestionDetail?.ExternalDocumentUri || ''}
              name="ExternalDocumentUri"
              control={control}
            />
          </Box>
        ) : (
          <Flex alignItems="center" justifyContent="center" height="100%">
            <Text fontSize={4}>
              Please select a question to add the details.
            </Text>
          </Flex>
        )}
      </Flex>
      <FileImage
        accountId={accountId}
        filesOnly
        account={`${accountInfoReturn?.CID}${'-'}${accountInfoReturn?.SID
          }${'-'}${accountInfoReturn?.PID}`}
        showPanel={showFileImage}
        togglePanel={toggleFileImage}
        createFolder={createBlobDirectory}
        uploadFile={uploadFile}
        deleteFile={deleteFile}
        buttonText={'Add to to question details'}
        addFileToEditor={(file: BlobType) => {
          if (file.Type !== 'image') {
            const hrefTag =
              "<a href='" +
              `${apiBaseUrl}/procedures/root/${file.Path}` +
              "/'>" +
              file.Name +
              '</a>';
            (window as any)?.tinymce?.activeEditor?.insertContent(hrefTag);
          } else {
            const hrefTag = `<img src="${file.Uri}" />`;
            (window as any)?.tinymce?.activeEditor?.insertContent(hrefTag);
          }
        }}
      />
      {isLoadingFile ? <Loader /> : null}
    </>
  );
}
